/**
 * A anatomia de tabela das 23 triagens — a MESMA da Planilha, uma constante só.
 *
 * Antes cada modal repintava o próprio cabeçalho (uns `text-xs`, outros
 * `text-sm`, alturas de linha de 42 a 50px), e duas triagens abertas em
 * sequência pareciam telas de produtos diferentes.
 */

/** Cabeçalho: 32px, 9px/700 caixa alta — igual ao da Planilha. */
export const TRIAGEM_TH =
  'h-8 px-2 text-left text-[9px] font-bold uppercase tracking-[1.3px] leading-none whitespace-nowrap bg-surface text-muted-label';

/** Linha de 33px (69a — antes 38, e antes ~46). O conteúdo é que cede, nunca a altura. */
export const TRIAGEM_TD = 'px-2 py-0 align-middle';

/* LARGURA DA TABELA (Douglas 29/10/2026: "não podemos ter uma funcionária sem
   ver as telas corretas"). O `TriagemShell` força 1.120 px em toda tabela com
   uma regra que vence o `min-w-*` da própria tabela; na página Triagens (área de
   1.131 px) a Razão Social ia a 0 na SMS e a 79 px nas do fluxo, e a Nota
   Fiscal cortava os últimos botões. A largura mínima de cada tabela vai no
   `style` (vence a regra do shell): abaixo dela a área rola para o lado, nunca
   espreme o nome nem esconde botão. */
export const TRIAGEM_LARGURA_MINIMA_NOME = 220;
/** Triagens no molde das colunas padrão (`TriagemStandardColGroup`). */
export const TRIAGEM_LARGURA_MINIMA_PADRAO = 1560;

export const TRIAGEM_TR_BASE = 'box-border h-[33px] border-b border-border/30 transition-colors';

/**
 * 1º da fila: filete bordô de 3px por dentro + fundo levemente quente.
 * É o "trabalhe este" sem precisar de texto.
 */
export const TRIAGEM_TR_TOPO =
  'bg-accent/40 hover:bg-accent/70 shadow-[inset_3px_0_0_hsl(var(--primary))]';

/** Travado pelo FIFO: esmaece, mas continua legível (não desabilita a leitura). */
export const TRIAGEM_TR_TRAVADO = 'opacity-75 hover:bg-muted/30';

export const TRIAGEM_TR_LIVRE = 'hover:bg-accent/50';

/** Zebra das ímpares — a mesma da Planilha, para a leitura correr na horizontal. */
export const TRIAGEM_TR_ZEBRA = 'bg-[#FDFBF9] dark:bg-[#241A1A]';

/** Célula de texto: sempre 1 linha com ellipsis — célula sem truncate desalinha a tabela inteira. */
export const TRIAGEM_CELULA = 'block truncate text-[11px] text-muted-foreground';
export const TRIAGEM_CELULA_FORTE = 'block truncate text-[12px] font-semibold text-foreground';
export const TRIAGEM_CELULA_NUM = 'block truncate font-display text-[12px] font-bold tabular-nums text-foreground';

/* ── OS BOTÕES DA LINHA — o padrão de construção (Douglas 17/09/2026) ────────
 *
 * "elas não têm um padrão de construção e isso é muito ruim... me dê exemplo de
 *  como ficaria: botões em destaque com os ícones que gostamos, o bordô nos
 *  botões, a tabela bem como o Kanban."
 *
 * O padrão JÁ existia na tela — o que não existia era em um lugar só. A mesma
 * string de ~300 caracteres estava copiada à mão **52 vezes em 14 arquivos**, e
 * o gradiente bordô outras 10. Copiada à mão significa: quem cria a próxima
 * triagem copia da vizinha, erra um token, e a tela nasce diferente sem
 * ninguém decidir que seria diferente.
 *
 * ⛔ Triagem nova USA estas constantes. Não reescreva a string.
 */

/** Ação comum da linha: quadrado 28px, ícone 14px, ganha bordô no hover. */
export const TRIAGEM_BTN =
  'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center p-0 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors hover:border-primary/50 hover:!text-primary dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px]';

/**
 * A ação PRINCIPAL da linha — bordô cheio.
 * ⚠️ UMA por linha. Duas ações em bordô sólido brigam entre si e a lei da casa
 * é um acento por view: se tudo é destaque, nada é.
 */
export const TRIAGEM_BTN_PRIMARIO =
  'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center p-0 rounded-[7px] bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] !text-white shadow-[0_3px_8px_-4px_rgba(91,10,13,0.6)] transition-all hover:bg-[linear-gradient(135deg,#5B0A0D,#6E1626)] hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)] [&_svg]:h-[14px] [&_svg]:w-[14px]';

/** Ligado/selecionado: bordô vazado, sem virar botão primário. */
export const TRIAGEM_BTN_ATIVO =
  'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center p-0 rounded-[7px] border border-primary/60 bg-primary/10 !text-primary transition-colors dark:bg-primary/20 [&_svg]:h-[14px] [&_svg]:w-[14px]';

/**
 * MANTER — "consultei e ainda não mudou nada" (Douglas 30/09/2026). É o
 * PRIMEIRO botão de toda triagem, em contorno bordô, e conta no placar
 * "Consulta hoje" como pedido consultado. Ver `AcoesTriagem.tsx`.
 */
export const TRIAGEM_BTN_MANTER =
  'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center p-0 rounded-[7px] border border-primary/70 bg-[#FAF8F5] !text-primary transition-colors hover:bg-primary/10 dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px]';

/**
 * Selo de estado da linha (Pendente · OK · Reprovado · sinalizado).
 *
 * ⛔ ÂMBAR NÃO EXISTE NA CASA. Pendência é DÍVIDA, e dívida é bordô — era
 * `text-primary` em 5 lugares. Verde é SÓ desfecho, e é por isso que "conferido"
 * o usa e nenhum botão de ação usa.
 */
export const TRIAGEM_SELO_BASE =
  'inline-flex items-center gap-0.5 rounded border border-border bg-muted px-1 py-0.5 text-[10px] font-medium';
/** Falta fazer / passado para o fim da fila / sinalizado: bordô. */
export const TRIAGEM_SELO_PENDENTE = 'text-primary dark:text-primary';
/** Desfecho: o verde da casa, o mesmo do Kanban. */
export const TRIAGEM_SELO_OK = 'text-[#075E54] dark:text-[#25D366]';
/** Falha de verdade — semáforo real, a exceção que a lei visual reserva. */
export const TRIAGEM_SELO_REPROVADO = 'text-danger dark:text-danger';
/** Quem abriu foi a IsabellA (Douglas 24/09/2026) — a mesma etiqueta "IA" do Painel CRM, com o nome dela. */
export const SELO_ISABELLA =
  'inline-flex flex-shrink-0 items-center rounded-[4px] border border-primary/40 px-[5px] text-[9px] font-bold leading-[13px] text-primary';
